import { useRef } from 'react';
import { Upload } from 'lucide-react';
import { FILE_UPLOAD_CONFIG } from '@/config';
import { handleError, ValidationError } from '@/errors';

interface LocalInpaintingUploadProps {
  readonly onImageSelected: (url: string) => void;
}

/* Empty-state placeholder shown inside the ImageViewer right panel when no local image has been selected yet.*/
export function LocalInpaintingUpload({ onImageSelected }: LocalInpaintingUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset value so the same file can be re-selected if the user wants to swap it
    e.target.value = '';

    if (!file) return;

    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    const supportedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.bmp'];
    const supportedMimeTypes = new Set<string>(FILE_UPLOAD_CONFIG.SUPPORTED_TYPES);
    const hasSupportedMimeType = supportedMimeTypes.has(fileType);
    const hasSupportedExtension = supportedExtensions.some((ext) => fileName.endsWith(ext));

    if (!hasSupportedMimeType && !hasSupportedExtension) {
      void handleError(
        new ValidationError(
          FILE_UPLOAD_CONFIG.MESSAGES.UNSUPPORTED_FORMAT(file.name),
          'fileValidation',
          'UNSUPPORTED_FORMAT',
        ),
        { context: 'Local image upload' },
      );
      return;
    }

    if (file.size > FILE_UPLOAD_CONFIG.MAX_SIZE) {
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
      void handleError(
        new ValidationError(
          FILE_UPLOAD_CONFIG.MESSAGES.FILE_TOO_LARGE(file.name, fileSizeMB),
          'fileValidation',
          'FILE_TOO_LARGE',
        ),
        { context: 'Local image upload' },
      );
      return;
    }

    const url = URL.createObjectURL(file);
    onImageSelected(url);
  };

  return (
    <div className="flex flex-col flex-1 justify-center items-center p-8 text-gray-500 dark:text-gray-400">
      <button
        type="button"
        onClick={handleClick}
        className="flex flex-col items-center gap-4 p-10 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Upload an image from your local drive for inpainting"
      >
        <Upload className="w-14 h-14 text-gray-400 dark:text-gray-500" />
        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
          Click to upload an image
        </span>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_UPLOAD_CONFIG.ACCEPT_ATTRIBUTE}
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
